import type { JSX, ReactNode, ComponentPropsWithoutRef, ChangeEvent } from 'react';
import { Children, cloneElement, useContext } from 'react';
import { styled, css } from 'styled-components';
import { isArray, isNotNil, isNotUndefined, isUndefined } from '@wistia/type-guards';
import { Text } from '../Text';
import { Label } from '../Label';
import { CheckboxGroupContext } from '../FormGroup/CheckboxGroup';
import { Checkbox } from '../Checkbox';
import { FormContext } from '../Form/Form';
import { FormControlLabelDescription } from '../../private/components/FormControlLabel/FormControlLabelDescription';
import { Stack } from '../Stack';

const inlineErrorStyles = css`
  grid-template-rows: 1fr auto;
  grid-template-areas: 'label-description input' '. error';
`;

const inlineBaseGridAreaStyles = css`
  grid-template-rows: 1fr;
  grid-template-areas: 'label-description input';
`;

const blockGridErrorStyles = css`
  grid-template-rows: auto 1fr auto;
  grid-template-areas: 'label-description' 'input' 'error';
`;

const StyledFormField = styled.div<{
  'data-label-position': 'block' | 'inline-compact' | 'inline';
  $hasError: boolean;
}>`
  --form-field-spacing: var(--wui-space-02);
  --form-field-spacing-inline: var(--wui-space-02);
  --form-field-error-color: var(--wui-color-text-secondary-error);

  display: grid;
  gap: var(--form-field-spacing);
  justify-content: flex-start;
  align-items: center;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto 1fr;

  &[data-label-position='inline-compact'] {
    gap: var(--form-field-spacing-inline);
    grid-template-columns: auto 1fr;
    ${inlineBaseGridAreaStyles}
    ${({ $hasError }) => $hasError && inlineErrorStyles}
  }

  &[data-label-position='inline'] {
    gap: var(--form-field-spacing-inline);
    grid-template-columns: minmax(auto, 1fr) 1fr;
    ${inlineBaseGridAreaStyles}
    ${({ $hasError }) => $hasError && inlineErrorStyles}
  }

  &[data-label-position='block'] {
    gap: var(--form-field-spacing);
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    grid-template-areas: 'label-description' 'input';
    ${({ $hasError }) => $hasError && blockGridErrorStyles}
  }
`;

const ErrorText = styled(Text)`
  grid-area: error;
`;

export type FormFieldProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * This should be a single child that is a valid form field (or can pretend to be one).
   */
  children: JSX.Element;
  /**
   * Additional descriptive text to display below the form field
   */
  description?: ReactNode;
  /**
   * Valid label text
   */
  label: ReactNode;
  /**
   * If an id isn't supplied, one is generated internally.
   */
  id?: string;
  /**
   * Will show an error message and put the child input into an invalid state
   */
  error?: ReactNode;
  /**
   * The name attribute of the field. It will map to the form data passed into `useFormState`.
   */
  name: string;
  /**
   * Use if label describes a required form component
   */
  required?: boolean;
  /**
   * A value that overrides the basic value. This should pair with `onChange`.
   */
  value?: string;
  /**
   * Setting to `inline` will have the label and input appear on the same line. Setting to `block` will have the label above the input.
   */
  labelPosition?: 'block' | 'inline-compact' | 'inline';
};

type ErrorsProps = {
  id: string;
  errors: ReactNode | ReactNode[];
};

const StyledErrorList = styled.ul`
  margin: 0;
  padding: 0;
  padding-left: var(--wui-space-04);
  display: flex;
  flex-direction: column;
  gap: var(--wui-space-01);
  grid-area: error;
`;

const ErrorMessages = ({ errors, id }: ErrorsProps) => {
  const isErrorArray = isArray(errors);
  const isMultipleErrors = isErrorArray && errors.length > 1;

  if (!isErrorArray) {
    return (
      <ErrorText
        key={id}
        colorScheme="error"
        id={id}
        prominence="secondary"
        variant="label4"
      >
        {errors}
      </ErrorText>
    );
  }

  if (!isMultipleErrors) {
    return (
      <ErrorText
        key={id}
        colorScheme="error"
        id={id}
        prominence="secondary"
        variant="label4"
      >
        {errors[0]}
      </ErrorText>
    );
  }

  return (
    <StyledErrorList>
      {errors.map((error, index) => (
        <Text
          // can't use error because it could be a ReactNode
          // eslint-disable-next-line react/no-array-index-key
          key={`${id}-${index}`}
          colorScheme="error"
          id={`${id}-${index}`}
          prominence="secondary"
          renderAs="li"
          variant="label4"
        >
          {error}
        </Text>
      ))}
    </StyledErrorList>
  );
};

/**
 * FormField is a compound component that combines a label, form control, and optional description and error message.
 */
export const FormField = ({
  children,
  description,
  error,
  id,
  label,
  labelPosition,
  name,
  required = false,
  value,
  ...props
}: FormFieldProps): JSX.Element => {
  const formState = useContext(FormContext);
  const checkboxGroup = useContext(CheckboxGroupContext);
  const defaultValue = formState.values[name] as string;
  const isIntegratedLabel = children.type === Checkbox;
  const computedId = id ?? `${formState.formId}-${name}`;
  const computedError = error ?? formState.errors[name];
  const descriptionId = `${computedId}-description`;
  const errorId = `${computedId}-error`;
  const ariaDescribedby = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;
  const hasDescription = isNotNil(description);
  const hasError = isNotNil(computedError);
  const shouldRenderLabelDescriptionWrapper = !isIntegratedLabel || hasDescription;
  let childProps = {
    name,
    id: computedId,
    label: isIntegratedLabel ? label : undefined,
    'aria-describedby': ariaDescribedby,
    'aria-invalid': hasError,
    style: { gridArea: 'input' },
    ...props,
  };

  if (isUndefined(value) && isNotUndefined(defaultValue)) {
    childProps = {
      ...childProps,
      defaultValue,
    };
  }

  if (isNotNil(checkboxGroup)) {
    const computedName = isNotNil(checkboxGroup.name) ? `${checkboxGroup.name}[${name}]` : name;

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (isNotUndefined(props.onChange)) {
        props.onChange(event);
      }

      if (isNotUndefined(checkboxGroup.onChange)) {
        checkboxGroup.onChange(event);
      }
    };

    childProps = {
      ...childProps,
      name: computedName,
      onChange: handleChange,
    };
  }

  Children.only(children); // eslint-disable-line @typescript-eslint/no-floating-promises

  return (
    <StyledFormField
      {...props}
      $hasError={hasError}
      data-label-position={labelPosition ?? formState.labelPosition}
    >
      {shouldRenderLabelDescriptionWrapper ? (
        <Stack
          direction="vertical"
          gap="space-01"
          style={{
            gridArea: 'label-description',
          }}
        >
          {!isIntegratedLabel && (
            <Label
              htmlFor={computedId}
              required={required}
            >
              {label}
            </Label>
          )}
          {hasDescription ? (
            <FormControlLabelDescription id={descriptionId}>
              {description}
            </FormControlLabelDescription>
          ) : null}
        </Stack>
      ) : null}
      {cloneElement(children, childProps)}

      {hasError ? (
        <ErrorMessages
          errors={computedError}
          id={errorId}
        />
      ) : null}
    </StyledFormField>
  );
};

FormField.displayName = 'FormField';
